import React from 'react';
import { motion } from 'framer-motion';
import Badge from './ui/Badge';
import Button from './ui/Button';
import SafeIcon from '../common/SafeIcon';
import { FiCheck, FiStar } from 'react-icons/fi';

const FeatureItem = ({ text }) => (
  <li className="flex items-start gap-3 mb-3">
    <SafeIcon icon={FiCheck} className="text-dc-teal-signal mt-1 flex-shrink-0" />
    <span className="text-dc-text/90 leading-relaxed">{text}</span>
  </li>
);

const Card = ({ title, badge, positioning, description, features, bestFor, ctaText, delay, variant = 'teal', price, sublabel, quarterlyNote, isFeatured = false }) => {
  const variantStyles = {
    teal: {
      gradient: 'from-dc-teal-signal to-dc-teal-accent',
      accent: 'bg-dc-teal-signal/5',
      border: 'border-dc-teal-deep/5'
    },
    gold: {
      gradient: 'from-dc-gold-1 to-dc-gold-2',
      accent: 'bg-dc-gold-1/5',
      border: 'border-dc-gold-1/30'
    }
  };

  const styles = variantStyles[variant];
  const href = title === 'AIMM Briefing Membership' 
    ? 'https://territory.digitalcowboy.io/checkout/?product_id=6405&product_type=membership'
    : 'https://territory.digitalcowboy.io/checkout/?product_id=6406&product_type=membership';

  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6, delay }}
      className={`bg-white rounded-3xl p-8 md:p-10 shadow-soft hover:shadow-float transition-shadow duration-500 flex flex-col h-full border ${styles.border} relative overflow-hidden ${isFeatured ? 'ring-2 ring-dc-gold-1/20' : ''}`}
    >
      {/* Subtle gradient accent at top */}
      <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${styles.gradient} opacity-50`}></div>

      {/* Featured badge */}
      {isFeatured && (
        <div className="absolute top-4 right-4 bg-gradient-to-r from-dc-gold-1 to-dc-gold-2 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
          <SafeIcon icon={FiStar} className="w-3 h-3" />
          <span>Living Network Tier</span>
        </div>
      )}

      <div className="mb-6">
        <Badge className="mb-4 inline-block">{badge}</Badge>
        <h2 className="text-2xl font-bold text-dc-teal-deep mb-4 pr-20">{title}</h2>
        
        {/* Pricing */}
        <div className="mb-4">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-dc-teal-deep">${price}</span>
            <span className="text-dc-text/60">/mo</span>
          </div>
          <p className="text-sm text-dc-text/60 mt-1">{sublabel}</p>
        </div>

        <p className="text-dc-teal-signal font-medium italic mb-4">{positioning}</p>
        <p className="text-dc-text/80 leading-relaxed">{description}</p>
      </div>

      <div className="flex-grow">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-dc-teal-deep/10 to-transparent my-6"></div>
        <h3 className="font-semibold text-dc-text mb-4">Bundle Includes:</h3>
        <ul className="mb-6">
          {features.map((f, i) => <FeatureItem key={i} text={f} />)}
        </ul>
        
        <div className={`${styles.accent} rounded-xl p-4 mb-6`}>
          <p className="text-sm text-dc-text/80 leading-relaxed">{quarterlyNote}</p>
        </div>
      </div>

      <div className={`${styles.accent} rounded-xl p-6 mb-8`}>
        <h4 className="font-display text-[10px] uppercase tracking-wider text-dc-teal-deep mb-2">Best For</h4>
        <p className="text-sm text-dc-text/80 leading-relaxed">{bestFor}</p>
      </div>

      <div className="mt-auto pt-4">
        <Button variant={isFeatured ? 'primary' : 'secondary'} href={href} fullWidth>{ctaText}</Button>
      </div>
    </motion.div>
  );
};

const MembershipCards = () => {
  return (
    <section id="memberships" className="py-16 px-6 max-w-6xl mx-auto">
      {/* Strategic frame */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <p className="text-lg text-dc-teal-deep/90 font-medium max-w-3xl mx-auto leading-relaxed">
          Briefing helps you learn what happened and what matters next. Build Your Network helps you meet who matters next.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        <Card 
          delay={0}
          variant="teal"
          title="AIMM Briefing Membership"
          badge="Get Informed"
          price="28.31"
          sublabel="Replay access + one quarterly check-in"
          positioning="Stay close to the signal."
          description="Get the recording, the takeaways, and the core meeting rhythm without stepping fully into the network layer."
          features={[
            "2-4 episodes of the full AIMM + AIRena half hour with Katrena Drake",
            "Speaker takeaways",
            "Strategic meeting breakdown",
            "Core Smart Connect access",
            "One 30-minute quarterly check-in",
            "RepUpgrade Reputation Report, valued at $199"
          ]}
          quarterlyNote="One 30-minute quarterly check-in with Joe and Katrena to hear what you are doing, where you are at, and what you are trying to clarify next."
          bestFor="AI builders, consultants, and operators who want the strategic breakdown and core meeting takeaways."
          ctaText="Get the Briefing"
        />
        
        <Card 
          delay={0.2}
          variant="gold"
          title="Build Your Network Membership"
          badge="Build Your Network"
          price="129.46"
          sublabel="Full access + network benefits"
          positioning="Build with people who heard the signal too."
          description="Get the briefing, then step into the rooms where the work can move. Build Your Network adds AI Industry Strategy Rooms, custom Territory channels, directory presence, WhatsApp connection, and quarterly guidance."
          features={[
            "Everything in AIMM Briefing Membership",
            "AI Industry Strategy Rooms",
            "Custom Territory channels",
            "WhatsApp Community",
            "AI Business Consultant Directory listing",
            "Quarterly guidance (90 mins)",
            "RepUpgrade Reputation Report, valued at $199"
          ]}
          quarterlyNote="90 minutes with Joe and/or Katrena each quarter to listen, sharpen direction, and help turn your current signal into a cleaner next move."
          bestFor="Professionals who want discoverability, stronger connections, and environments where relationships actually begin."
          ctaText="Build Your Network"
          isFeatured={true}
        />
      </div>
    </section>
  );
};

export default MembershipCards;